import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { HelpLayout } from "../help/HelpLayout";
import {
  articleSnippet,
  articleSummary,
  helpGroups,
  searchArticles,
  HELP_ARTICLES,
  type HelpArticle,
  type Snippet,
} from "../help/corpus";
/**
 * Раздел «Справка» — поиск и оглавление с подписями (правая панель адреса `/help`).
 *
 * РОЛЕЙ ЗДЕСЬ НЕТ СОВСЕМ. Фильтр по ролям снят 22.08.2026, подпись «кому адресована» — 23.08.2026
 * (оба — решения владельца на приёмке, записаны в плане §3): все статьи одинаково открыты всем,
 * и напоминать читателю о ролях на каждой строке незачем. Ролевые пометки остались в файлах
 * статей как сырьё для памяток Д1/Д2 — в интерфейс они не выводятся.
 *
 * Поиск — вхождение подстроки по заголовку, тексту и ключевым словам (морфологии нет намеренно,
 * план §8). В строке выдачи показывается ФРАГМЕНТ с найденным словом: без него список
 * заголовков не отвечает на вопрос «а туда ли я попал».
 */

const inputCls =
  "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";

/**
 * Найденное место в статье. Подсветку рисует экран (`mark` с токенами палитры, правило 9) —
 * сама выборка фрагмента разметки не знает.
 */
function SnippetLine({ snippet }: { snippet: Snippet }) {
  return (
    <p className="mt-1 text-[13px] leading-[1.6] text-muted">
      {snippet.before}
      <mark className="rounded-[var(--radius-s)] bg-soon-bg px-0.5 font-bold text-soon-fg">
        {snippet.match}
      </mark>
      {snippet.after}
    </p>
  );
}

/** Строка выдачи: заголовок, кому адресована и место находки (либо обычная подпись). */
function ResultRow({ article, query }: { article: HelpArticle; query: string }) {
  const snippet = articleSnippet(article, query);
  return (
    <li className="rounded-[var(--radius-m)] border border-border bg-surface p-3">
      <Link to={`/help/${article.id}`} className="text-[15px] font-bold text-accent underline">
        {article.title}
      </Link>
      {snippet === null ? (
        <p className="mt-1 text-[13px] text-muted">{articleSummary(article)}</p>
      ) : (
        <SnippetLine snippet={snippet} />
      )}
    </li>
  );
}

export function HelpPage() {
  const [query, setQuery] = useState("");
  const found = useMemo(() => searchArticles(query), [query]);
  const searching = query.trim() !== "";

  return (
    <HelpLayout>
      <section aria-labelledby="help-title">
        <h1 id="help-title" className="text-[20px] font-bold text-ink">
          Справка
        </h1>
        <p className="mt-2 max-w-[var(--w-text)] text-[14px] text-muted">
          Как устроена система и почему она ведёт себя именно так. Выберите статью в оглавлении
          слева или найдите её по слову из текста.
        </p>

        <div className="mt-4 w-[360px] max-w-full">
          <label htmlFor="help-search" className="block text-[13px] font-bold text-ink">
            Поиск по справке
          </label>
          <input
            id="help-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="слово из статьи, например «сторно»"
            className={`${inputCls} mt-1`}
          />
        </div>

        {searching && (
          <p className="mt-3 text-[13px] text-muted">
            Найдено статей: {found.length} из {HELP_ARTICLES.length}
          </p>
        )}

        {searching && found.length === 0 ? (
          <div className="mt-4 rounded-[var(--radius-m)] border border-border bg-surface p-4">
            <p className="text-[14px] text-ink">Ничего не найдено.</p>
            <p className="mt-2 max-w-[var(--w-text)] text-[14px] text-muted">
              Попробуйте другое слово — поиск ищет точное вхождение, без учёта окончаний. Все статьи
              справки перечислены слева.
            </p>
          </div>
        ) : searching ? (
          <section aria-label="Результаты поиска">
            <ul className="mt-4 space-y-2">
              {found.map((article) => (
                <ResultRow key={article.id} article={article} query={query} />
              ))}
            </ul>
          </section>
        ) : (
          // Без запроса правая панель — то же оглавление, но с подписями: дерево слева даёт
          // заголовки, а выбрать по одному заголовку из шестнадцати трудно.
          helpGroups().map((group) => (
            <section key={group} aria-label={group} className="mt-6">
              <h2 className="text-[15px] font-bold uppercase tracking-wide text-muted">{group}</h2>
              <ul className="mt-2 space-y-2">
                {HELP_ARTICLES.filter((a) => a.group === group).map((article) => (
                  <li
                    key={article.id}
                    className="rounded-[var(--radius-m)] border border-border bg-surface p-3"
                  >
                    <Link
                      to={`/help/${article.id}`}
                      className="text-[15px] font-bold text-accent underline"
                    >
                      {article.title}
                    </Link>
                    <p className="mt-1 text-[13px] text-muted">{articleSummary(article)}</p>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </section>
    </HelpLayout>
  );
}
